import { useMemo, useState } from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import { useQuery } from '@apollo/client';

import { DESC, ASC } from 'constants/sortDirections';
import { DEFAULT_LIMIT } from '../constants';
import { GET_SHOWCASE_PROJECTS } from '../graphql/queries';
import { pageInfoFromData } from 'helpers/graphql-utils';

export type ShowcaseTableRowNode = {
  createdAt: string;
  id: string;
  featuredImage: {
    assetPreviewThumbnail: string;
  };
  project: {
    databaseId: string;
    formattedLine1: string;
    id: string;
    coordinates?: {
      lat: number;
      lon: number;
    };
  };
  title: string;
  visible: boolean;
};

type ShowcaseTableRow = {
  node: ShowcaseTableRowNode;
};

type PaginationFilters = {
  after: string | null;
  before: string | null;
  first: number | null;
  last: number | null;
};

export default function useShowcaseTableData() {
  const [orderBy, setOrderBy] = useState({
    field: 'CREATED_AT',
    direction: DESC,
  });
  const [limit, setLimit] = useState(DEFAULT_LIMIT.value);
  const {
    data,
    error,
    loading,
    fetchMore,
    refetch,
    variables: queryVariables,
  } = useQuery(GET_SHOWCASE_PROJECTS, {
    variables: {
      ...orderBy,
      first: limit,
      last: null,
      after: null,
      before: null,
    },
  });

  const pageInfo = pageInfoFromData(
    data,
    'viewer.company.showcase.showcaseProjects',
  );

  const handleFetchMore = (variables: PaginationFilters) =>
    fetchMore({
      variables: {
        ...variables,
        ...orderBy,
      },
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });

  const handleLimitSelection = (value: number) => {
    setLimit(value);
    return refetch({
      ...orderBy,
      first: value,
      last: null,
      after: null,
      before: null,
    });
  };

  const fetchNextPage = () =>
    handleFetchMore({
      first: limit,
      last: null,
      after: pageInfo.endCursor,
      before: null,
    });

  const fetchPreviousPage = () =>
    handleFetchMore({
      first: null,
      last: limit,
      after: null,
      before: pageInfo.startCursor,
    });

  const rows = useMemo(() => {
    const edges: ShowcaseTableRow[] = get(
      data,
      'viewer.company.showcase.showcaseProjects.edges',
      [],
    );
    return edges.map(({ node }) => node);
  }, [data]);

  const showcasePublicIdentifier = get(
    data,
    'viewer.company.showcase.publicIdentifier',
    null,
  );

  const requestSort = (field: string) => {
    let direction = DESC;
    if (orderBy.direction === DESC && orderBy.field === field) {
      direction = ASC;
    }
    setOrderBy({ field, direction });
    refetch({ field, direction });
  };

  const sortingIconClassName = (field: string) => {
    const { field: orderField, direction } = orderBy;
    return classnames({
      'mdi-menu-up': orderField === field && direction === ASC,
      'mdi-menu-down': orderField === field && direction === DESC,
    });
  };

  return {
    error,
    fetchNextPage,
    fetchPreviousPage,
    handleLimitSelection,
    hasNextPage: pageInfo.hasNextPage,
    hasPreviousPage: pageInfo.hasPreviousPage,
    loading,
    requestSort,
    refetch,
    rows,
    showcasePublicIdentifier,
    sortingIconClassName,
    queryVariables,
  };
}
